import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, CloudOff, Eraser, Keyboard,
  LayoutGrid, Loader2, Maximize, Moon, Sun, WifiOff, X,
} from "lucide-react";

import Shimmer from "../Common/Shimmer";
import { AppButton, Callout, IconButton, cn } from "../ui";
import ExamClock from "./ExamClock";
import ExamDialog from "./ExamDialog";
import QuestionPane from "./QuestionPane";
import CodingQuestion from "./CodingQuestion";
import QuestionPalette, { PaletteLegend, paletteCounts } from "./QuestionPalette";
import SubmitReview from "./SubmitReview";
import { PassageView, passageRanges, rangeLabel } from "../QuizPassage";
import useExamTimer from "./useExamTimer";
import useAutosaveQueue from "./useAutosaveQueue";
import useAntiCheat from "./useAntiCheat";

/**
 * ExamRunner — the exam-taking screen, shared by enrolled students and public
 * exam candidates.
 *
 * It is transport-agnostic on purpose: it receives an ADAPTER and never imports
 * apiClient, so a fix lands for both populations at once.
 *
 * Adapter contract (see adapters/):
 *   loadAttempt()                 -> { attempt, questions, exam, policy }
 *   saveAnswer(questionId, patch) -> { timeLeftSeconds?, expired? }
 *   reportEvent(type, meta)       -> { autoSubmitted?, warning? }
 *   heartbeat()                   -> { timeLeftSeconds, ended?, autoSubmitted? }
 *   submit({ timeSpent })         -> { submitted }   (idempotent server-side)
 *   passageFileUrl(passageId)     -> a short-lived link to a passage's PDF (optional)
 *   runCode(questionId, body)     -> a coding problem's sample-test verdicts (optional)
 *   nextSection(from)             -> { sectionClock, last? } — timed sections in order (optional)
 *
 * A coding problem (`questionType: 'coding'`) is answered with `{ code, codeLanguage }`.
 * `attempt.sectionClock` is present only when timed sections run in order: then
 * only the open section can be answered, it has its own countdown, and when it
 * ends — or the candidate finishes it — the next one opens and it is closed.
 *
 * `loadAttempt` may also return `passages` — what a set of questions is asked
 * about (a reading passage, a case document). A question names its passage by
 * id in `question.passage`; the passage is drawn beside the question.
 *   onFinished(result)            navigation is the host page's business
 *
 * A host may also pass `banner` (a strip drawn above the exam's own top bar) and
 * `statusText` (said in place of the save status) — a staff preview uses both,
 * because "All answers saved" is untrue when nothing is.
 *
 * Built for a full hall on one small server:
 *   - the countdown re-renders only itself (useExamTimer);
 *   - heartbeats are spread out, never on the same second for everyone;
 *   - when time runs out, answers are flushed at once but the submit itself is
 *     spread over a few seconds, and retried with backoff — the server treats a
 *     repeated submit as the same submit, so retrying is always safe.
 *
 * Styling: Tailwind on the app's tokens. Spec: docs/public-exams/EXAM_RUNNER_UX_SPEC.md
 */

const FONT_STEPS = [0.9, 1, 1.15, 1.3];
const FONT_KEY = "vc-exam-font-scale";

// How long an automatic submit may wait before it fires, so a hall whose clocks
// all reach zero together does not arrive in the same second. Answers are flushed
// BEFORE this wait, inside the server's grace window.
const AUTO_SUBMIT_SPREAD_MS = 3000;
const SUBMIT_RETRY_MS = [1500, 3000, 6000, 10000, 15000];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const spread = (ms) => Math.round(ms * (0.7 + Math.random() * 0.6));

/** A submit failure worth retrying: no answer at all, a timeout, a 5xx, a 429. */
const retryable = (err) => {
  const status = err?.response?.status;
  return !status || status >= 500 || status === 429 || status === 408;
};

/** The one-line save state under the title. Quiet when all is well. */
function SaveStatus({ status, online }) {
  const icon = !online ? <WifiOff size={13} aria-hidden="true" />
    : status.kind === "saving" ? <Loader2 size={13} aria-hidden="true" className="tw-animate-spin" />
      : status.kind === "pending" ? <CloudOff size={13} aria-hidden="true" />
        : <Check size={13} aria-hidden="true" />;
  const tone = !online ? "tw-text-danger-fg"
    : status.kind === "pending" ? "tw-text-warning-fg"
      : "tw-text-content-muted";
  return (
    <span className={cn("tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1", tone)} role="status" aria-live="polite">
      {icon}
      <span className="tw-truncate">{status.text}</span>
    </span>
  );
}

function CenteredMessage({ title, children }) {
  return (
    <div className="tw-fixed tw-inset-0 tw-z-drawer-scrim tw-flex tw-items-center tw-justify-center tw-bg-surface tw-p-6">
      <div className="tw-flex tw-max-w-md tw-flex-col tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-8 tw-py-10 tw-text-center tw-shadow-card">
        <AlertTriangle size={28} aria-hidden="true" className="tw-text-content-muted" />
        <h2 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-content">{title}</h2>
        <p className="tw-m-0 tw-text-sm tw-text-content-muted">{children}</p>
      </div>
    </div>
  );
}

const SHORTCUTS = [
  ["1–9 or A–J", "Choose that option"],
  ["→ or N", "Save & next"],
  ["← or P", "Previous question"],
  ["F or M", "Mark for review"],
  ["C", "Clear response"],
  ["Ctrl + Enter", "Review & submit"],
  ["?", "Show this list"],
];

export default function ExamRunner({ adapter, onFinished, onFatal, banner = null, statusText = "" }) {
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState(null);
  const [data, setData] = useState(null); // { attempt, questions, exam, policy }
  const [answers, setAnswers] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visited, setVisited] = useState(() => new Set([0]));
  const [reviewOpen, setReviewOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitNote, setSubmitNote] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [unsaved, setUnsaved] = useState([]);
  const [autoReason, setAutoReason] = useState(null);
  const [toast, setToast] = useState(null);
  // Timed sections in order: the server's clock for the open section, and the
  // "finish this section?" confirmation. null on every other paper.
  const [sectionClock, setSectionClock] = useState(null);
  const [sectionConfirm, setSectionConfirm] = useState(false);
  // Passages a candidate folded away on a small screen, by id.
  const [foldedPassages, setFoldedPassages] = useState(() => new Set());
  const [fontIdx, setFontIdx] = useState(() => {
    try {
      const i = FONT_STEPS.indexOf(Number(window.localStorage.getItem(FONT_KEY)));
      return i >= 0 ? i : 1;
    } catch { return 1; }
  });
  const [dark, setDark] = useState(
    () => typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark"
  );

  const startedAtRef = useRef(Date.now());
  const finishedRef = useRef(false);
  const submittingRef = useRef(false);
  const scrollRef = useRef(null);
  // Read by handlers that must not be re-created on every answer or navigation.
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const policy = useMemo(() => data?.policy || {}, [data]);
  const examMeta = data?.exam || {};
  const questions = useMemo(() => data?.questions || [], [data]);
  const passageById = useMemo(() => {
    const map = {};
    for (const p of data?.passages || []) map[String(p._id)] = p;
    return map;
  }, [data]);
  const ranges = useMemo(() => passageRanges(questions), [questions]);

  // ── sections ───────────────────────────────────────────────────────
  // Where each section's questions start and end on the paper; a paper of one
  // section shows no section bar. On a paper whose timed sections run in order,
  // each also says whether it is finished, open or still to come, and only the
  // open one can be reached.
  const sectionBounds = useMemo(() => {
    const progress = data?.attempt?.sectionProgress || [];
    if (!examMeta.isSectioned || progress.length < 2) return [];
    let start = 0;
    return progress.map((s, i) => {
      const bound = {
        title: s.title,
        timeLimit: s.timeLimit || 0,
        start,
        end: start + (s.questionCount || 0),
        status: sectionClock
          ? (i < sectionClock.index ? "done" : i === sectionClock.index ? "open" : "locked")
          : undefined,
      };
      start = bound.end;
      return bound;
    });
  }, [data, examMeta.isSectioned, sectionClock]);
  const openSection = sectionClock ? sectionBounds[sectionClock.index] || null : null;
  const openSectionRef = useRef(openSection);
  openSectionRef.current = openSection;

  // ── load ───────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const payload = await adapter.loadAttempt();
        if (cancelled) return;
        setData(payload);
        setAnswers(payload.attempt?.answers || []);
        startedAtRef.current = payload.attempt?.startedAt
          ? new Date(payload.attempt.startedAt).getTime()
          : Date.now();
        const clock = payload.attempt?.sectionClock;
        if (clock?.sequential) {
          setSectionClock(clock);
          // Open on the first question of the section the clock says is open.
          const counts = (payload.attempt.sectionProgress || []).map((s) => s.questionCount || 0);
          const start = counts.slice(0, clock.index).reduce((sum, n) => sum + n, 0);
          setCurrentIndex(start);
          setVisited(new Set([start]));
        }
      } catch (err) {
        if (cancelled) return;
        setFatal(err?.response?.data?.message || "This exam could not be loaded.");
        onFatal?.(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter]);

  // ── finishing ──────────────────────────────────────────────────────
  const antiCheatRef = useRef(null);
  const queueRef = useRef(null);

  const finish = useCallback((result) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    antiCheatRef.current?.suppress(true);
    onFinished?.(result);
  }, [onFinished]);

  const doSubmit = useCallback(async ({ auto = false, reason = "" } = {}) => {
    if (finishedRef.current || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError("");
    setSubmitNote(auto ? "Saving your last answers…" : "");
    antiCheatRef.current?.suppress(true);

    // Drain the queue FIRST. Submitting with answers still in flight is how a
    // candidate loses the last thing they typed.
    const drain = await queueRef.current?.flushNow?.();
    if (drain && !drain.ok && !auto) {
      setUnsaved(drain.failedQuestionIds);
      submittingRef.current = false;
      setSubmitting(false);
      antiCheatRef.current?.suppress(false);
      setReviewOpen(true);
      return;
    }

    // Spread a hall's automatic submits over a few seconds. Answers are already
    // on the server, so the wait costs nothing.
    if (auto) {
      setSubmitNote("Submitting your exam…");
      await sleep(Math.random() * AUTO_SUBMIT_SPREAD_MS);
    }

    const timeSpent = Math.floor((Date.now() - startedAtRef.current) / 1000);
    let lastErr = null;
    const tries = auto ? SUBMIT_RETRY_MS.length + 1 : 3;
    for (let i = 0; i < tries; i++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await adapter.submit({ timeSpent });
        finish({ ...result, auto, reason });
        return;
      } catch (err) {
        lastErr = err;
        if (!retryable(err) || i === tries - 1) break;
        setSubmitNote("The server is busy — retrying…");
        // eslint-disable-next-line no-await-in-loop
        await sleep(spread(SUBMIT_RETRY_MS[Math.min(i, SUBMIT_RETRY_MS.length - 1)]));
      }
    }

    submittingRef.current = false;
    setSubmitting(false);
    setSubmitNote("");
    antiCheatRef.current?.suppress(false);
    setSubmitError(
      lastErr?.response?.data?.message
      || "We could not reach the server. Your answers are saved — check your connection and try again."
    );
    setReviewOpen(true);
  }, [adapter, finish]);

  const expire = useCallback(() => {
    if (finishedRef.current) return;
    setAutoReason("time");
    setPaletteOpen(false);
    setShowShortcuts(false);
    setSectionConfirm(false);
    doSubmit({ auto: true, reason: "time_expired" });
  }, [doSubmit]);

  // A paper opened after its last section's time ran out is simply submitted.
  useEffect(() => {
    if (sectionClock?.finished) expire();
  }, [sectionClock, expire]);

  // ── timer ──────────────────────────────────────────────────────────
  const timer = useExamTimer({
    initialSeconds: data?.attempt?.timeLeftSeconds ?? null,
    onExpire: expire,
    // Sections on their own clocks announce their own time.
    onMilestone: (m) => { if (!sectionClock) setToast({ kind: "time", text: m.label }); },
  });
  const { reconcile } = timer;

  // ── timed sections in order ────────────────────────────────────────
  // The open section's own countdown. The paper's clock above still runs and
  // still ends the exam; this one moves the candidate from section to section.
  const sectionClockRef = useRef(sectionClock);
  sectionClockRef.current = sectionClock;
  const sectionExpireRef = useRef(null);
  const sectionTimer = useExamTimer({
    initialSeconds: data?.attempt?.sectionClock?.sequential ? data.attempt.sectionClock.secondsLeft : null,
    onExpire: () => sectionExpireRef.current?.(),
    onMilestone: (m) => setToast({
      kind: "time",
      text: m.key === "half" ? "Half of this section's time is left" : `${m.label} in this section`,
    }),
  });
  const { restart: restartSection, reconcile: reconcileSection } = sectionTimer;

  /** Adopt the server's word on which section is open, and for how long. */
  const applyClock = useCallback((clock) => {
    if (!clock?.sequential) return;
    const before = sectionClockRef.current;
    sectionClockRef.current = clock;
    setSectionClock(clock);
    if (clock.finished) { expire(); return; }
    restartSection(clock.secondsLeft);
    if (before && before.index === clock.index) return;
    const progress = data?.attempt?.sectionProgress || [];
    const start = progress.slice(0, clock.index).reduce((sum, s) => sum + (s.questionCount || 0), 0);
    // The new section's range arrives with the next render; until then nothing clamps.
    openSectionRef.current = null;
    setCurrentIndex(start);
    setVisited((prev) => new Set(prev).add(start));
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    if (before) setToast({ kind: "time", text: `${progress[clock.index]?.title || "The next section"} has started` });
  }, [data, expire, restartSection]);

  const advancingRef = useRef(false);
  /**
   * Close the open section and open the next. The section's last answers are
   * saved FIRST — once it closes, nothing in it can be written. On the last
   * section there is nothing to open: the clock running out submits the exam,
   * and finishing it early is "Review & submit".
   */
  const advanceSection = useCallback(async ({ auto = false } = {}) => {
    const clock = sectionClockRef.current;
    if (!clock?.sequential || advancingRef.current || finishedRef.current) return;
    if (clock.last) {
      if (auto) expire();
      return;
    }
    advancingRef.current = true;
    setSectionConfirm(false);
    let retry = false;
    try {
      await queueRef.current?.flushNow?.();
      let res = null;
      let lastErr = null;
      for (let i = 0; i < 4 && !res; i++) {
        try {
          // eslint-disable-next-line no-await-in-loop
          res = await adapter.nextSection(clock.index);
        } catch (err) {
          lastErr = err;
          if (err?.response?.status === 409) {
            finish({ submitted: true, auto: true, reason: "submitted" });
            return;
          }
          if (!retryable(err)) break;
          // eslint-disable-next-line no-await-in-loop
          await sleep(spread(SUBMIT_RETRY_MS[Math.min(i, SUBMIT_RETRY_MS.length - 1)]));
        }
      }
      if (!res) {
        retry = auto;
        setToast({
          kind: "error",
          text: lastErr?.response?.data?.message || "The next section could not be opened. Check your connection.",
        });
        return;
      }
      if (res.last) { expire(); return; }
      applyClock(res.sectionClock);
    } finally {
      advancingRef.current = false;
      // A section that ran out while offline keeps trying; the server moves the
      // candidate on by its own clock either way.
      if (retry) setTimeout(() => sectionExpireRef.current?.(), 10000);
    }
  }, [adapter, applyClock, expire, finish]);
  sectionExpireRef.current = () => advanceSection({ auto: true });

  // ── autosave ───────────────────────────────────────────────────────
  const saveAnswer = useCallback(async (questionId, patch) => {
    const res = await adapter.saveAnswer(questionId, patch);
    if (res?.timeLeftSeconds != null) reconcile(res.timeLeftSeconds);
    if (res?.sectionSecondsLeft != null) reconcileSection(res.sectionSecondsLeft);
    return res;
  }, [adapter, reconcile, reconcileSection]);

  const onRejected = useCallback((_questionId, message, body) => {
    // The section this answer belongs to has closed — the screen is behind the
    // server's clock. Catch up rather than keep the candidate in a dead section.
    if (body?.errorCode === "SECTION_CLOSED" && body.sectionClock) {
      applyClock(body.sectionClock);
      setToast({ kind: "error", text: "That section has closed. Your answers in it were saved up to then." });
      return;
    }
    setToast({ kind: "error", text: message || "One answer could not be saved." });
  }, [applyClock]);

  const queue = useAutosaveQueue({
    attemptId: data?.attempt?._id,
    saveAnswer,
    onExpired: expire,
    onRejected,
  });
  queueRef.current = queue;

  // ── anti-cheat ─────────────────────────────────────────────────────
  const antiCheat = useAntiCheat({
    policy,
    active: !!data && !finishedRef.current && !loading,
    onEvent: useCallback(async (type, meta) => {
      try {
        const res = await adapter.reportEvent(type, meta);
        if (res?.autoSubmitted) {
          setAutoReason(type);
          finish({ submitted: true, auto: true, reason: type, message: res.message });
          return;
        }
        if (res?.warning) antiCheatRef.current?.showWarning(res.warning);
      } catch {
        // A violation report that fails must never interrupt the exam.
      }
    }, [adapter, finish]),
  });
  antiCheatRef.current = antiCheat;

  // ── heartbeat ──────────────────────────────────────────────────────
  // Spread out: everyone in a hall loads at 10:00 sharp, and a fixed interval
  // would keep them beating on the same second for the whole paper.
  useEffect(() => {
    if (!data || !adapter.heartbeat) return undefined;
    const every = Math.max(10, policy?.heartbeatSeconds || 30) * 1000;
    let timeoutId;
    let stopped = false;
    const beat = async () => {
      if (stopped || finishedRef.current) return;
      try {
        const res = await adapter.heartbeat();
        if (res?.timeLeftSeconds != null) reconcile(res.timeLeftSeconds);
        if (res?.ended || res?.autoSubmitted) {
          setAutoReason(res.reason === "time_expired" ? "time" : (res.reason || "time"));
          finish({ submitted: true, auto: true, reason: res.reason });
          return;
        }
      } catch {
        // Offline. The save status already tells the candidate.
      }
      if (!stopped) timeoutId = setTimeout(beat, spread(every));
    };
    timeoutId = setTimeout(beat, Math.random() * every);
    return () => { stopped = true; clearTimeout(timeoutId); };
  }, [data, adapter, policy?.heartbeatSeconds, reconcile, finish]);

  // ── answering ──────────────────────────────────────────────────────
  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentIndex];

  const updateAnswer = useCallback((patch) => {
    const index = currentIndexRef.current;
    const cur = answersRef.current[index];
    if (!cur || finishedRef.current) return;
    const merged = { ...cur, ...patch };
    // A coding problem's answer is its code; the server decides the same way.
    merged.isAnswered = cur.kind === "coding"
      ? String(merged.code || "").trim().length > 0
      : (merged.selectedOptionIds?.length > 0) || (String(merged.textAnswer || "").trim().length > 0);
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = { ...prev[index], ...patch, isAnswered: merged.isAnswered };
      return next;
    });
    queueRef.current?.enqueue(cur.question, patch);
  }, []);

  const setFlag = useCallback((isFlagged) => updateAnswer({ isFlagged }), [updateAnswer]);
  // A coding problem is reset from its own editor ("Reset code"), never here.
  const clearResponse = useCallback(() => {
    if (answersRef.current[currentIndexRef.current]?.kind === "coding") return;
    updateAnswer({ selectedOptionIds: [], textAnswer: "" });
  }, [updateAnswer]);

  const goTo = useCallback((idx) => {
    setCurrentIndex((prev) => {
      let next = Math.max(0, Math.min(idx, questions.length - 1));
      // Timed sections in order: nothing outside the open section is reachable.
      const open = openSectionRef.current;
      if (open) next = Math.max(open.start, Math.min(next, open.end - 1));
      if (next !== prev && scrollRef.current) scrollRef.current.scrollTop = 0;
      return next;
    });
  }, [questions.length]);

  const goNext = useCallback(() => goTo(currentIndexRef.current + 1), [goTo]);
  const goPrev = useCallback(() => goTo(currentIndexRef.current - 1), [goTo]);

  useEffect(() => {
    setVisited((prev) => (prev.has(currentIndex) ? prev : new Set(prev).add(currentIndex)));
  }, [currentIndex]);

  const openReview = useCallback(() => {
    setPaletteOpen(false);
    setReviewOpen(true);
  }, []);

  // ── keyboard ───────────────────────────────────────────────────────
  // Dialogs take the keyboard themselves (ExamDialog listens in the capture
  // phase), so this only ever sees keys meant for the paper.
  useEffect(() => {
    if (!data) return undefined;

    const handler = (e) => {
      if (finishedRef.current || submittingRef.current) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      // Typing "5" into a numeric answer must not navigate to question 5.
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "Enter") { e.preventDefault(); openReview(); }
        return;
      }
      if (e.altKey) return;

      const key = e.key.toLowerCase();
      const index = currentIndexRef.current;
      const q = questions[index];
      const a = answersRef.current[index];

      if (key === "arrowright" || key === "n") { e.preventDefault(); goNext(); return; }
      if (key === "arrowleft" || key === "p") { e.preventDefault(); goPrev(); return; }
      if (key === "f" || key === "m") { e.preventDefault(); setFlag(!a?.isFlagged); return; }
      if (key === "c") { e.preventDefault(); clearResponse(); return; }
      if (key === "?") { e.preventDefault(); setShowShortcuts(true); return; }

      // 1–9 and a–j choose an option, when the question has one there.
      const opts = q?.options || [];
      if (!opts.length) return;
      let optIdx = -1;
      if (/^[1-9]$/.test(key)) optIdx = Number(key) - 1;
      else if (/^[a-j]$/.test(key)) optIdx = key.charCodeAt(0) - 97;
      if (optIdx >= 0 && optIdx < opts.length) {
        e.preventDefault();
        const id = String(opts[optIdx]._id);
        const sel = (a?.selectedOptionIds || []).map(String);
        if (q.questionType === "multi_select") {
          updateAnswer({ selectedOptionIds: sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id] });
        } else {
          updateAnswer({ selectedOptionIds: [id] });
        }
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [data, questions, goNext, goPrev, setFlag, clearResponse, updateAnswer, openReview]);

  // ── preferences ────────────────────────────────────────────────────
  const changeFont = (delta) => {
    setFontIdx((i) => {
      const next = Math.max(0, Math.min(FONT_STEPS.length - 1, i + delta));
      try { window.localStorage.setItem(FONT_KEY, String(FONT_STEPS[next])); } catch { /* noop */ }
      return next;
    });
  };

  // The exam follows the app's theme, and the switch here IS the app's switch —
  // the same attribute and the same saved preference ThemeContext reads.
  const toggleTheme = () => {
    const next = dark ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    try { window.localStorage.setItem("theme", next); } catch { /* noop */ }
    setDark(!dark);
  };

  const currentSection = sectionBounds.findIndex((b) => currentIndex >= b.start && currentIndex < b.end);

  // App-level floating UI (the install banner) hides itself while this is set.
  useEffect(() => {
    document.body.setAttribute("data-exam-active", "");
    return () => document.body.removeAttribute("data-exam-active");
  }, []);

  // ── toast auto-dismiss ─────────────────────────────────────────────
  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(id);
  }, [toast]);

  // ── render ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="tw-fixed tw-inset-0 tw-z-drawer-scrim tw-flex tw-flex-col tw-bg-surface" aria-busy="true">
        <div className="tw-border-0 tw-border-b tw-border-solid tw-border-line tw-bg-surface-raised tw-px-6 tw-py-3">
          <Shimmer height={32} />
        </div>
        <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-3xl tw-flex-col tw-gap-4 tw-p-6">
          <Shimmer height={28} />
          <Shimmer height={96} />
          <Shimmer height={56} count={4} />
        </div>
      </div>
    );
  }

  if (fatal) return <CenteredMessage title="This exam is not available">{fatal}</CenteredMessage>;

  if (!questions.length) {
    return (
      <CenteredMessage title="No questions to show">
        This exam has no questions. Please contact the invigilator.
      </CenteredMessage>
    );
  }

  const counts = paletteCounts(answers, currentIndex, visited);
  const donePct = counts.total ? Math.round((counts.totalAnswered / counts.total) * 100) : 0;
  const marks = currentQuestion?.marks ?? 1;
  const penalty = examMeta.negativeMarking
    ? (currentQuestion?.negativeMarks || examMeta.negativeMarkValue || 0)
    : 0;
  const isLast = currentIndex === questions.length - 1;
  const isCoding = currentQuestion?.questionType === "coding";
  // Timed sections in order: the open section's last question finishes the
  // section — unless it is the paper's last section, which is submitted.
  const sectionEnd = !!openSection && !sectionClock?.last && currentIndex === openSection.end - 1;
  const openTitle = openSection?.title || "this section";
  const sectionUnanswered = openSection
    ? answers.slice(openSection.start, openSection.end).filter((a) => !a.isAnswered).length
    : 0;
  const nextTitle = sectionClock ? sectionBounds[sectionClock.index + 1]?.title : null;
  const atStart = openSection ? currentIndex <= openSection.start : currentIndex === 0;
  const fsGate = policy.fullscreenRequired && antiCheat.fullscreenSupported && !antiCheat.fullscreenActive;
  const selectable = !policy.disableCopyPaste;
  const passageId = currentQuestion?.passage ? String(currentQuestion.passage) : null;
  const currentPassage = passageId ? passageById[passageId] : null;
  const passageFolded = !!passageId && foldedPassages.has(passageId);
  const togglePassage = () => setFoldedPassages((prev) => {
    const next = new Set(prev);
    if (next.has(passageId)) next.delete(passageId);
    else next.add(passageId);
    return next;
  });
  const loadPassageFile = adapter.passageFileUrl && currentPassage
    ? () => adapter.passageFileUrl(currentPassage._id)
    : null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-drawer-scrim tw-flex tw-flex-col tw-bg-surface tw-font-sans tw-text-content">
      {banner}
      {/* ── Top bar ── */}
      <header className="tw-relative tw-flex tw-shrink-0 tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-line tw-bg-surface-raised tw-px-4 tw-py-2.5 sm:tw-px-6">
        <div className="tw-min-w-0 tw-flex-1">
          <h1 className="tw-m-0 tw-truncate tw-text-[0.9375rem] tw-font-semibold tw-leading-snug tw-text-content" title={examMeta.title}>
            {examMeta.title}
          </h1>
          <div className="tw-mt-0.5 tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-xs tw-text-content-muted">
            {examMeta.candidateName && (
              <>
                <span className="tw-hidden tw-truncate sm:tw-inline">{examMeta.candidateName}</span>
                <span className="tw-hidden sm:tw-inline" aria-hidden="true">·</span>
              </>
            )}
            {statusText
              ? <span className="tw-truncate">{statusText}</span>
              : <SaveStatus status={queue.status} online={queue.online} />}
          </div>
        </div>

        {sectionClock ? (
          <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
            <span className="tw-hidden tw-max-w-[11rem] tw-truncate tw-text-xs tw-text-content-muted md:tw-inline">
              {openTitle}
            </span>
            <ExamClock clock={sectionTimer.clock} hasLimit={sectionTimer.hasLimit} />
          </div>
        ) : (
          <ExamClock clock={timer.clock} hasLimit={timer.hasLimit} />
        )}

        <div className="tw-hidden tw-items-center tw-gap-1 md:tw-flex" role="group" aria-label="Text size">
          <AppButton type="button" variant="ghost" size="sm" onClick={() => changeFont(-1)} disabled={fontIdx === 0} aria-label="Smaller text">
            A−
          </AppButton>
          <AppButton type="button" variant="ghost" size="sm" onClick={() => changeFont(1)} disabled={fontIdx === FONT_STEPS.length - 1} aria-label="Larger text">
            A+
          </AppButton>
        </div>

        <IconButton
          icon={dark ? Sun : Moon}
          label={dark ? "Switch to light mode" : "Switch to dark mode"}
          aria-pressed={dark}
          variant="ghost"
          size="sm"
          className="tw-hidden sm:tw-inline-flex"
          onClick={toggleTheme}
        />

        {/* Outlined: the paper's one filled button is the next step in the question card. */}
        <AppButton type="button" variant="outline" size="sm" onClick={openReview} disabled={submitting} aria-label="Submit exam">
          <span className="sm:tw-hidden">Submit</span>
          <span className="tw-hidden sm:tw-inline">Submit exam</span>
        </AppButton>

        {/* Progress — answered, not visited. */}
        <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-0.5" aria-hidden="true">
          <div className="tw-h-full tw-bg-primary tw-transition-[width] tw-duration-300" style={{ width: `${donePct}%` }} />
        </div>
      </header>

      {/* ── Sections ── */}
      {sectionBounds.length > 0 && (
        <nav aria-label="Sections" className="tw-flex tw-shrink-0 tw-gap-1 tw-overflow-x-auto tw-border-0 tw-border-b tw-border-solid tw-border-line tw-bg-surface-raised tw-px-3 sm:tw-px-5">
          {sectionBounds.map((b, i) => {
            const on = i === currentSection;
            const closed = b.status === "done" || b.status === "locked";
            return (
              <button
                type="button"
                key={`${b.title}-${i}`}
                onClick={() => goTo(b.start)}
                disabled={closed}
                aria-current={on ? "true" : undefined}
                className={cn(
                  "tw-relative tw-flex tw-shrink-0 tw-items-baseline tw-gap-1.5 tw-whitespace-nowrap tw-border-0 tw-bg-transparent tw-px-3 tw-py-3 tw-text-sm tw-focus-ring-inset",
                  on ? "tw-font-semibold tw-text-primary" : "tw-text-content-muted hover:tw-text-content",
                  closed ? "tw-cursor-not-allowed tw-opacity-60" : "tw-cursor-pointer",
                )}
              >
                {b.title}
                <span className="tw-text-xs tw-font-normal tw-text-content-subtle">
                  {b.end - b.start}{b.timeLimit ? ` · ${b.timeLimit}m` : ""}
                  {b.status === "done" ? " · finished" : ""}
                </span>
                {on && <span className="tw-absolute tw-inset-x-3 tw-bottom-0 tw-h-0.5 tw-rounded-full tw-bg-primary" aria-hidden="true" />}
              </button>
            );
          })}
        </nav>
      )}

      {/* ── Body ── */}
      <div className="tw-flex tw-min-h-0 tw-flex-1">
        <main ref={scrollRef} className="tw-relative tw-min-w-0 tw-flex-1 tw-overflow-y-auto">
          {/* Watermark — deters photographing the paper with a second device. */}
          {policy.watermark && examMeta.watermarkText && (
            <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-inset-0 tw-overflow-hidden tw-select-none">
              <div className="tw-grid tw-grid-cols-3 tw-gap-x-16 tw-gap-y-24 tw-p-10 tw-opacity-[0.06]">
                {Array.from({ length: 18 }).map((_, i) => (
                  <span key={i} className="tw--rotate-[24deg] tw-whitespace-nowrap tw-text-lg tw-font-semibold tw-text-content">
                    {examMeta.watermarkText}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div
            className={cn(
              "tw-relative tw-mx-auto tw-flex tw-w-full tw-flex-col tw-gap-4 tw-px-4 tw-py-5 sm:tw-px-6 min-[992px]:tw-py-8",
              currentPassage || isCoding ? "tw-max-w-[90rem]" : "tw-max-w-3xl",
            )}
          >
            {antiCheat.warning && (
              <Callout
                tone="warning"
                role="alert"
                action={<IconButton icon={X} label="Dismiss" variant="ghost" size="xs" onClick={antiCheat.dismissWarning} />}
              >
                {antiCheat.warning.text}
              </Callout>
            )}

            {/* With a passage: the passage and the question side by side on a wide
                screen — the passage stays put while the question scrolls, and keeps
                its own scroll across the set because it stays mounted — and stacked,
                foldable, below that. */}
            <div
              className={cn(
                currentPassage && "tw-grid tw-gap-4 min-[1200px]:tw-grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] min-[1200px]:tw-items-start",
              )}
            >
              {currentPassage && (
                <aside
                  key={passageId}
                  aria-label="Passage for this question"
                  className={cn(
                    "tw-rounded-lg tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-p-5 tw-shadow-card sm:tw-p-6",
                    "tw-overflow-y-auto tw-overscroll-contain max-[1199px]:tw-max-h-[60vh]",
                    "min-[1200px]:tw-sticky min-[1200px]:tw-top-8 min-[1200px]:tw-max-h-[calc(100dvh-10.5rem)]",
                  )}
                >
                  <div className="tw-mb-2 tw-flex tw-justify-end min-[1200px]:tw-hidden">
                    <AppButton type="button" variant="ghost" size="xs" onClick={togglePassage} aria-expanded={!passageFolded}>
                      {passageFolded
                        ? <><ChevronDown size={14} aria-hidden="true" /> Show passage</>
                        : <><ChevronUp size={14} aria-hidden="true" /> Hide passage</>}
                    </AppButton>
                  </div>
                  {passageFolded ? (
                    <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-content min-[1200px]:tw-hidden">
                      {currentPassage.title || "Passage"}
                      <span className="tw-font-normal tw-text-content-muted"> · {rangeLabel(ranges[passageId])}</span>
                    </p>
                  ) : null}
                  <div className={cn(passageFolded && "max-[1199px]:tw-hidden")}>
                    <PassageView
                      passage={currentPassage}
                      rangeLabel={rangeLabel(ranges[passageId])}
                      loadFileUrl={loadPassageFile}
                      cacheKey={`${data?.attempt?._id || "attempt"}:${passageId}`}
                      selectable={selectable}
                      fontScale={FONT_STEPS[fontIdx]}
                    />
                  </div>
                </aside>
              )}
              <article className="tw-rounded-lg tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-p-5 tw-shadow-card sm:tw-p-8">
                {isCoding ? (
                  <CodingQuestion
                    key={currentQuestion._id}
                    question={currentQuestion}
                    answer={currentAnswer}
                    index={currentIndex}
                    total={questions.length}
                    marks={marks}
                    onAnswer={updateAnswer}
                    onFlag={setFlag}
                    fontScale={FONT_STEPS[fontIdx]}
                    selectable={selectable}
                    runCode={adapter.runCode ? (body) => adapter.runCode(currentQuestion._id, body) : null}
                    blockPaste={!!policy.disableCopyPaste}
                    onPasteBlocked={() => antiCheat.report?.("copyPaste", { action: "paste" })}
                  />
                ) : (
                  <QuestionPane
                    question={currentQuestion}
                    answer={currentAnswer}
                    index={currentIndex}
                    total={questions.length}
                    marks={marks}
                    penalty={penalty}
                    onAnswer={updateAnswer}
                    onFlag={setFlag}
                    fontScale={FONT_STEPS[fontIdx]}
                    selectable={selectable}
                  />
                )}

                {/* Actions — desktop. A phone gets the bottom bar instead. */}
                <div className="tw-mt-8 tw-hidden tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-pt-5 min-[992px]:tw-flex">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <AppButton type="button" variant="outline" onClick={goPrev} disabled={atStart}>
                      <ChevronLeft size={16} aria-hidden="true" /> Previous
                    </AppButton>
                    {!isCoding && (
                      <AppButton type="button" variant="ghost" onClick={clearResponse} disabled={!currentAnswer?.isAnswered}>
                        <Eraser size={15} aria-hidden="true" /> Clear response
                      </AppButton>
                    )}
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    {!isLast && !sectionEnd && (
                      <AppButton type="button" variant="outline" onClick={() => { setFlag(true); goNext(); }}>
                        Mark &amp; next
                      </AppButton>
                    )}
                    {sectionEnd ? (
                      <AppButton type="button" variant="default" onClick={() => setSectionConfirm(true)}>
                        Finish {openTitle} <ChevronRight size={16} aria-hidden="true" />
                      </AppButton>
                    ) : isLast ? (
                      <AppButton type="button" variant="default" onClick={openReview}>
                        Review &amp; submit
                      </AppButton>
                    ) : (
                      <AppButton type="button" variant="default" onClick={goNext}>
                        Save &amp; next <ChevronRight size={16} aria-hidden="true" />
                      </AppButton>
                    )}
                  </div>
                </div>

                {/* Clear — phone. */}
                {currentAnswer?.isAnswered && !isCoding && (
                  <div className="tw-mt-5 min-[992px]:tw-hidden">
                    <AppButton type="button" variant="ghost" size="sm" onClick={clearResponse}>
                      <Eraser size={15} aria-hidden="true" /> Clear response
                    </AppButton>
                  </div>
                )}
              </article>
            </div>
          </div>
        </main>

        {/* ── Rail — desktop ── */}
        <aside
          aria-label="Exam navigator"
          className="tw-hidden tw-w-[19rem] tw-shrink-0 tw-flex-col tw-overflow-y-auto tw-border-0 tw-border-l tw-border-solid tw-border-line tw-bg-surface-raised min-[992px]:tw-flex"
        >
          <div className="tw-flex tw-flex-col tw-gap-5 tw-p-5 tw-pb-36">
            <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
              <h2 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">Questions</h2>
              <span className="tw-text-xs tw-tabular-nums tw-text-content-muted">
                {counts.totalAnswered}/{counts.total} answered
              </span>
            </div>
            <QuestionPalette
              answers={answers}
              currentIndex={currentIndex}
              visited={visited}
              sections={sectionBounds}
              onNavigate={goTo}
            />
            <PaletteLegend counts={counts} className="tw-border-0 tw-border-t tw-border-solid tw-border-line tw-pt-4" />
            {sectionClock && !sectionClock.last && (
              <AppButton type="button" variant="outline" className="tw-w-full" onClick={() => setSectionConfirm(true)} disabled={submitting}>
                Finish {openTitle}
              </AppButton>
            )}
            <AppButton type="button" variant="secondary" className="tw-w-full" onClick={openReview} disabled={submitting}>
              Review &amp; submit
            </AppButton>
            <AppButton type="button" variant="ghost" size="sm" className="tw-self-start" onClick={() => setShowShortcuts(true)}>
              <Keyboard size={15} aria-hidden="true" /> Keyboard shortcuts
            </AppButton>
          </div>
        </aside>
      </div>

      {/* ── Bottom bar — phone ── */}
      <nav
        aria-label="Question navigation"
        className="tw-flex tw-shrink-0 tw-items-center tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-bg-surface-raised tw-px-3 tw-pt-2.5 tw-pb-[max(0.625rem,env(safe-area-inset-bottom))] min-[992px]:tw-hidden"
      >
        <IconButton icon={ChevronLeft} label="Previous question" variant="outline" onClick={goPrev} disabled={atStart} />
        <AppButton
          type="button"
          variant="outline"
          className="tw-flex-1"
          onClick={() => setPaletteOpen(true)}
          aria-label={`Question ${currentIndex + 1} of ${counts.total}, ${counts.totalAnswered} answered — open question list`}
        >
          <LayoutGrid size={15} aria-hidden="true" />
          <span className="tw-tabular-nums">{currentIndex + 1} / {counts.total}</span>
        </AppButton>
        {sectionEnd ? (
          <AppButton type="button" variant="default" onClick={() => setSectionConfirm(true)}>Finish section</AppButton>
        ) : isLast ? (
          <AppButton type="button" variant="default" onClick={openReview}>Review</AppButton>
        ) : (
          <AppButton type="button" variant="default" onClick={goNext} aria-label="Next question">
            Next <ChevronRight size={16} aria-hidden="true" />
          </AppButton>
        )}
      </nav>

      {/* ── Question list — phone ── */}
      <ExamDialog
        open={paletteOpen}
        title="Questions"
        description={`${counts.totalAnswered} of ${counts.total} answered`}
        onClose={() => setPaletteOpen(false)}
        footer={
          <AppButton type="button" variant="default" className="tw-w-full sm:tw-w-auto" onClick={openReview}>
            Review &amp; submit
          </AppButton>
        }
      >
        <div className="tw-flex tw-flex-col tw-gap-5">
          <QuestionPalette
            answers={answers}
            currentIndex={currentIndex}
            visited={visited}
            sections={sectionBounds}
            onNavigate={(i) => { setPaletteOpen(false); goTo(i); }}
          />
          <PaletteLegend counts={counts} className="tw-border-0 tw-border-t tw-border-solid tw-border-line tw-pt-4" />
        </div>
      </ExamDialog>

      {/* ── Review & submit ── */}
      {(reviewOpen || autoReason) && (
        <SubmitReview
          answers={answers}
          visited={visited}
          currentIndex={currentIndex}
          sections={sectionBounds}
          onJump={(i) => { setReviewOpen(false); setUnsaved([]); setSubmitError(""); goTo(i); }}
          onBack={() => { setReviewOpen(false); setUnsaved([]); setSubmitError(""); }}
          onConfirm={() => doSubmit({ auto: !!autoReason, reason: autoReason ? "time_expired" : "" })}
          submitting={submitting}
          submitNote={submitNote}
          submitError={submitError}
          negativeMarking={examMeta.negativeMarking}
          negativeMarkValue={examMeta.negativeMarkValue}
          unsaved={unsaved}
          online={queue.online}
          autoReason={autoReason}
        />
      )}

      {/* ── Finish a timed section ── */}
      <ExamDialog
        open={sectionConfirm}
        size="sm"
        title={`Finish ${openTitle}?`}
        description="You cannot come back to it once you move on."
        onClose={() => setSectionConfirm(false)}
        footer={(
          <>
            <AppButton type="button" variant="outline" onClick={() => setSectionConfirm(false)}>Keep working</AppButton>
            <AppButton type="button" variant="default" onClick={() => advanceSection()}>
              {nextTitle ? `Go to ${nextTitle}` : "Finish section"}
            </AppButton>
          </>
        )}
      >
        <p className="tw-m-0 tw-text-sm tw-text-content">
          {sectionUnanswered > 0
            ? `${sectionUnanswered} question${sectionUnanswered === 1 ? " is" : "s are"} unanswered in this section.`
            : "Every question in this section is answered."}
          {" "}Time left on it is not carried over.
        </p>
      </ExamDialog>

      {/* ── Shortcuts ── */}
      <ExamDialog
        open={showShortcuts}
        size="sm"
        title="Keyboard shortcuts"
        onClose={() => setShowShortcuts(false)}
      >
        <dl className="tw-m-0 tw-flex tw-flex-col">
          {SHORTCUTS.map(([keys, what]) => (
            <div key={keys} className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-2.5 tw-border-0 tw-border-t tw-border-solid tw-border-line first:tw-border-t-0">
              <dt className="tw-text-sm tw-text-content-muted">{what}</dt>
              <dd className="tw-m-0">
                <kbd className="tw-rounded-[6px] tw-border tw-border-solid tw-border-line tw-bg-surface tw-px-2 tw-py-0.5 tw-font-mono tw-text-xs tw-text-content tw-shadow-none">
                  {keys}
                </kbd>
              </dd>
            </div>
          ))}
        </dl>
      </ExamDialog>

      {/* Fullscreen gate — content is HIDDEN behind it, not dimmed. A candidate
          who exits fullscreen to read something else must not be able to read
          the question at the same time. */}
      {fsGate && (
        <div role="dialog" aria-modal="true" aria-labelledby="exam-fs-title" className="tw-fixed tw-inset-0 tw-z-critical tw-flex tw-items-center tw-justify-center tw-bg-surface tw-p-6">
          <div className="tw-flex tw-max-w-md tw-flex-col tw-items-center tw-gap-3 tw-text-center">
            <Maximize size={28} aria-hidden="true" className="tw-text-content-muted" />
            <h2 id="exam-fs-title" className="tw-m-0 tw-text-lg tw-font-semibold tw-text-content">Fullscreen is required</h2>
            <p className="tw-m-0 tw-text-sm tw-text-content-muted">
              This exam must be taken in fullscreen. Leaving fullscreen is recorded and may be
              reviewed. Your timer is still running.
            </p>
            <AppButton type="button" variant="default" className="tw-mt-2" onClick={antiCheat.enterFullscreen}>
              <Maximize size={15} aria-hidden="true" /> Return to fullscreen
            </AppButton>
          </div>
        </div>
      )}

      {/* ── Non-blocking notice ── */}
      {toast && (
        <div className="tw-pointer-events-none tw-fixed tw-inset-x-0 tw-top-16 tw-z-toast tw-flex tw-justify-center tw-px-4">
          <div role="status" className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-surface-dark tw-px-4 tw-py-2 tw-text-sm tw-text-on-surface-dark tw-shadow-popover tw-animate-drop-in">
            {toast.kind === "error" && <AlertTriangle size={15} aria-hidden="true" />}
            {toast.text}
          </div>
        </div>
      )}
    </div>
  );
}
